import { useTranslation } from 'react-i18next';
import {
  advertorialChoices,
  applicationChoices,
  contentTypeChoices,
  deletedContentChoices,
  draftContentChoices,
  flagsChoices,
  photoSalesChoices,
  publicationStatusChoices,
  publishStatusChoices,
  templateContentChoices,
  userContributedChoices,
  getSelectSx,
} from 'components/Inputs/Dropdown/dropdownConsts';
import { NumRangeDropdownInput } from 'components/Inputs/Dropdown/NumRangeDropdownInput';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { TextDateRangeDropdownInput } from 'components/Inputs/Dropdown/TextDateRangeDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import { Fragment } from 'react';
import { useSearch } from '../../../../SearchContextProvider';
import { useAdvancedSearchContext } from '../../useAdvancedSearchContext';
import {
  useGetFetchAndFormatAuthors,
  useGetFetchAndFormatPublications,
  useGetFetchAndFormatSections,
  useGetFetchAndFormatWorkflows,
  useGetFetchAndFormatUnlabeledAuthors,
  cacheAuthorChoice,
  getCachedAuthorChoices,
} from '../hooks';

export const EditorialFilterFields = () => {
  const { t } = useTranslation();
  const { validListFilter } = useSearch();
  const { showFilters, handleQpValueUpdate } = useAdvancedSearchContext();

  // Editorial Search Specific
  const { fetchAndFormatSections } = useGetFetchAndFormatSections();
  const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
  const { fetchAndFormatUnlabeledAuthors } = useGetFetchAndFormatUnlabeledAuthors();
  const { fetchAndFormatWorkflows } = useGetFetchAndFormatWorkflows();
  const { fetchAndFormatPublications } = useGetFetchAndFormatPublications();

  return (
    <Fragment>
      <SelectDropdownInput
        staticChoices={contentTypeChoices}
        inputLabel={t('blox.editorial.asset.type.label')}
        value={validListFilter?.type || []}
        updateValue={(event, value) => handleQpValueUpdate('type', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showType}
        multiple
        selectSx={getSelectSx}
      />

      <TextDateRangeDropdownInput
        inputLabel={t('blox.editorial.asset.start_time.label')}
        value={[
          validListFilter?.['start_time[min]'] || '',
          validListFilter?.['start_time[max]'] || '',
        ]}
        updateValue={(event, value) => {
          handleQpValueUpdate('start_time[min]', value[0]);
          handleQpValueUpdate('start_time[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={!showFilters?.showStartTime}
      />

      <TextDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_slug.label')}
        value={validListFilter?.print_slug || ''}
        updateValue={(event, value) => handleQpValueUpdate('print_slug', value)}
        debounceUpdate={300}
        hidden={!showFilters?.showPrintSlug}
        filterPlaceholderText={t('blox.editorial.metadata.group.print_slug.help')}
        applyChangesOnPopoverClose
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={draftContentChoices}
        inputLabel={t('blox.editorial.asset.draft.label')}
        value={validListFilter?.draft || ''}
        updateValue={(event, value) => handleQpValueUpdate('draft', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showDraft}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.asset.sections.label')}
        value={validListFilter?.section || []}
        updateValue={(event, values) => handleQpValueUpdate('section', values)}
        fetchDataFn={fetchAndFormatSections}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showSection}
        multiple
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.metadata.group.workflow.label')}
        value={validListFilter?.workflow || []}
        updateValue={(event, value) => handleQpValueUpdate('workflow', value)}
        fetchDataFn={fetchAndFormatWorkflows}
        infiniteScroll={false}
        showFilter
        showFooter={false}
        showChipBox={true}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showWorkflow}
        multiple
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={publishStatusChoices}
        inputLabel={t('blox.editorial.asset.properties.no_publish.label_alt')}
        value={validListFilter?.publish_status || []}
        updateValue={(event, value) => handleQpValueUpdate('publish_status', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showPublishStatus}
        multiple
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.asset.authors.label_one')}
        value={
          Array.isArray(validListFilter.author)
            ? validListFilter.author[0] || ''
            : validListFilter.author || ''
        }
        updateValue={(event, values) => handleQpValueUpdate('author', values)}
        fetchDataFn={fetchAndFormatAuthors}
        fetchUnlabeledItemsFn={fetchAndFormatUnlabeledAuthors}
        getCachedItemsFn={getCachedAuthorChoices}
        onChoiceSelected={cacheAuthorChoice}
        hidden={!showFilters?.showAuthor}
        isDataPrivate={true}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={deletedContentChoices}
        inputLabel={t('blox.general.deleted')}
        value={validListFilter?.deleted || ''}
        updateValue={(event, value) => handleQpValueUpdate('deleted', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showDeleted}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={templateContentChoices}
        inputLabel={t('blox.general.template')}
        value={validListFilter?.template || ''}
        updateValue={(event, value) => handleQpValueUpdate('template', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showTemplate}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={flagsChoices}
        inputLabel={t('blox.editorial.asset.flags.label')}
        value={validListFilter?.flag || []}
        updateValue={(event, value) => handleQpValueUpdate('flag', value)}
        infiniteScroll={false}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showFlag}
        multiple
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={advertorialChoices}
        inputLabel={t('blox.general.value_added_content')}
        value={validListFilter?.advertorial || ''}
        updateValue={(event, value) => handleQpValueUpdate('advertorial', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showAdvertorial}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={userContributedChoices}
        inputLabel={t('blox.general.contributed')}
        value={validListFilter?.contributed || ''}
        updateValue={(event, value) => handleQpValueUpdate('contributed', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showContributed}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={applicationChoices}
        inputLabel={t('blox.editorial.asset.properties.app.label')}
        value={validListFilter?.app || []}
        updateValue={(event, value) => handleQpValueUpdate('app', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showFilters?.showApp}
        multiple
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={photoSalesChoices}
        inputLabel={t('blox.editorial.asset.properties.photo_sales.label')}
        value={validListFilter?.photo_sales || ''}
        updateValue={(event, value) => handleQpValueUpdate('photo_sales', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showPhotoSales}
        selectSx={getSelectSx}
      />

      <SelectDropdownInput
        staticChoices={photoSalesChoices}
        inputLabel={t('blox.editorial.metadata.group.print_ready.label')}
        value={validListFilter?.print_ready || ''}
        updateValue={(event, value) => handleQpValueUpdate('print_ready', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showPrintReady}
        selectSx={getSelectSx}
      />

      <NumRangeDropdownInput
        valueMode="normal"
        inputLabel={t('blox.editorial.metadata.group.print_depth.label')}
        inputPlaceholderText="Print Depth Range"
        value={[
          validListFilter['print_depth[min]'] || '',
          validListFilter['print_depth[max]'] || '',
        ]}
        updateValue={(event, value) => {
          handleQpValueUpdate('print_depth[min]', value[0]);
          handleQpValueUpdate('print_depth[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={!showFilters?.showPrintDepthRange}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_publication.label')}
        value={validListFilter?.print_publication || ''}
        updateValue={(event, values) => handleQpValueUpdate('print_publication', values)}
        fetchDataFn={fetchAndFormatPublications}
        hidden={!showFilters?.showPrintPublication}
        isDataPrivate={true}
        selectSx={getSelectSx}
      />

      <TextDateRangeDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_publication_date_range.label')}
        inputPlaceholderText={t(
          'blox.editorial.metadata.group.print_publication_date_range.label',
        )}
        value={[
          validListFilter?.['print_publication_date[min]'] || '',
          validListFilter?.['print_publication_date[max]'] || '',
        ]}
        updateValue={(event, value) => {
          handleQpValueUpdate('print_publication_date[min]', value[0]);
          handleQpValueUpdate('print_publication_date[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={!showFilters?.showPrintPublicationDateRange}
        outputFormat="YYYY-MM-DD"
        displayFormat="pretty"
      />

      <SelectDropdownInput
        staticChoices={publicationStatusChoices}
        inputLabel={t('blox.editorial.metadata.group.print_publication_status.label')}
        value={validListFilter?.print_publication_status || ''}
        updateValue={(event, value) =>
          handleQpValueUpdate('print_publication_status', value)
        }
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showFilters?.showPrintPublicationStatus}
        selectSx={getSelectSx}
      />
    </Fragment>
  );
};
